import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  MapPin, 
  Package, 
  Truck, 
  CheckCircle, 
  Clock, 
  Phone, 
  MessageCircle,
  ArrowLeft,
  Navigation,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Delivery status steps
const DELIVERY_STEPS = [
  { key: 'pending_payment', label: 'Payment', icon: Clock, description: 'Waiting for payment' },
  { key: 'searching_driver', label: 'Finding Driver', icon: Truck, description: 'Finding a driver' },
  { key: 'driver_assigned', label: 'Driver Assigned', icon: CheckCircle, description: 'Driver on the way' },
  { key: 'picked_up', label: 'Picked Up', icon: Package, description: 'Package collected' },
  { key: 'in_transit', label: 'In Transit', icon: Navigation, description: 'On the way to you' },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle, description: 'Package delivered' }
];

const DeliveryTracking = () => {
  const { deliveryId } = useParams();
  const navigate = useNavigate();
  
  const [delivery, setDelivery] = useState(null);
  const [driver, setDriver] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eta, setEta] = useState(null);
  
  // Rating modal state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  
  const wsRef = useRef(null);
  const mapRef = useRef(null);
  const driverMarkerRef = useRef(null);

  // Submit delivery rating
  const submitRating = async () => {
    if (selectedRating === 0) {
      toast({ title: "Please select a rating", variant: "destructive" });
      return;
    }
    
    setIsSubmittingRating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/users/${delivery.user_id}/deliveries/${deliveryId}/rate`, {
        rating: selectedRating,
        comment: ratingComment || null
      });
      
      toast({ title: "Thank you!", description: "Your rating has been submitted" });
      setShowRatingModal(false);
      setDelivery(prev => ({ ...prev, user_rating: selectedRating }));
    } catch (err) {
      toast({ 
        title: "Error", 
        description: err.response?.data?.detail || "Could not submit rating",
        variant: "destructive"
      });
    } finally {
      setIsSubmittingRating(false);
    }
  };

  // Show browser push notification
  const showPushNotification = (title, body) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/jstyle-delivery-logo.jpg',
        badge: '/jstyle-delivery-logo.jpg',
        vibrate: [200, 100, 200]
      });
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification(title, { body, icon: '/jstyle-delivery-logo.jpg' });
        }
      });
    }
  };

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Show rating modal when delivery is completed and not yet rated
  useEffect(() => {
    if (delivery && 
        (delivery.status === 'completed' || delivery.status === 'delivered') && 
        !delivery.user_rating && 
        !showRatingModal) {
      // Small delay to let user see the completed status first
      const timer = setTimeout(() => {
        setShowRatingModal(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [delivery, showRatingModal]);

  // Fetch delivery details
  const fetchDelivery = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/rides/${deliveryId}`);
      setDelivery(response.data);
      
      // Fetch driver details if assigned
      if (response.data.driver_id) {
        const driverResponse = await axios.get(`${BACKEND_URL}/api/drivers/${response.data.driver_id}`);
        setDriver(driverResponse.data);
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching delivery:', err);
      setError('Delivery not found');
      setIsLoading(false);
    }
  }, [deliveryId]);

  // Initialize map with multi-stop support
  const initializeMap = useCallback(() => {
    if (!delivery || !window.L || mapRef.current) return;
    
    const pickupCoords = delivery.pickup?.lat && delivery.pickup?.lng 
      ? [delivery.pickup.lat, delivery.pickup.lng]
      : [40.7128, -74.0060]; // Default NYC
    
    const map = window.L.map('tracking-map').setView(pickupCoords, 13);
    
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);
    
    // Collect all route points for the polyline
    const routePoints = [];
    
    // Add pickup marker (green)
    if (delivery.pickup?.lat && delivery.pickup?.lng) {
      routePoints.push([delivery.pickup.lat, delivery.pickup.lng]);
      window.L.marker([delivery.pickup.lat, delivery.pickup.lng], {
        icon: window.L.divIcon({
          className: 'custom-marker',
          html: `<div style="background: #10b981; width: 28px; height: 28px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 12px;">P</div>`,
          iconSize: [28, 28],
          iconAnchor: [14, 14]
        })
      }).addTo(map).bindPopup('<b>Pickup</b><br>' + (delivery.pickup?.address || 'Pickup Location'));
    }
    
    // Add stop markers (amber/orange with numbers)
    const stops = delivery.stops || [];
    stops.forEach((stop, index) => {
      if (stop?.lat && stop?.lng) {
        routePoints.push([stop.lat, stop.lng]);
        const isCompleted = delivery.completed_stops?.includes(index);
        window.L.marker([stop.lat, stop.lng], {
          icon: window.L.divIcon({
            className: 'custom-marker',
            html: `<div style="background: ${isCompleted ? '#6b7280' : '#f59e0b'}; width: 26px; height: 26px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 11px;">${index + 1}</div>`,
            iconSize: [26, 26],
            iconAnchor: [13, 13]
          })
        }).addTo(map).bindPopup(`<b>Stop ${index + 1}</b>${isCompleted ? ' ✓' : ''}<br>` + (stop?.address || `Stop ${index + 1}`));
      } else if (stop?.address) {
        // Stop without coordinates - just show in list
      }
    });
    
    // Add dropoff marker (red)
    if (delivery.dropoff?.lat && delivery.dropoff?.lng) {
      routePoints.push([delivery.dropoff.lat, delivery.dropoff.lng]);
      window.L.marker([delivery.dropoff.lat, delivery.dropoff.lng], {
        icon: window.L.divIcon({
          className: 'custom-marker',
          html: `<div style="background: #ef4444; width: 28px; height: 28px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 12px;">D</div>`,
          iconSize: [28, 28],
          iconAnchor: [14, 14]
        })
      }).addTo(map).bindPopup('<b>Dropoff</b><br>' + (delivery.dropoff?.address || 'Dropoff Location'));
    }
    
    // Draw route polyline connecting all points
    if (routePoints.length >= 2) {
      window.L.polyline(routePoints, {
        color: '#f59e0b',
        weight: 4,
        opacity: 0.7,
        dashArray: '10, 10'
      }).addTo(map);
    }
    
    // Fit map to show all markers
    if (routePoints.length >= 2) {
      const bounds = window.L.latLngBounds(routePoints);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
    
    mapRef.current = map;
  }, [delivery]);

  // Update driver marker on map
  const updateDriverMarker = useCallback((lat, lng) => {
    if (!mapRef.current || !window.L) return;
    
    if (driverMarkerRef.current) {
      driverMarkerRef.current.setLatLng([lat, lng]);
    } else {
      driverMarkerRef.current = window.L.marker([lat, lng], {
        icon: window.L.divIcon({
          className: 'driver-marker',
          html: `<div style="background: #f59e0b; width: 32px; height: 32px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>
          </div>`,
          iconSize: [32, 32],
          iconAnchor: [16, 16]
        })
      }).addTo(mapRef.current);
    }
    
    // Center map on driver with animation
    mapRef.current.panTo([lat, lng], { animate: true, duration: 0.5 });
    
    // Calculate ETA using real traffic data API
    if (delivery?.dropoff?.lat && delivery?.dropoff?.lng) {
      fetchRealETA(lat, lng, delivery.dropoff.lat, delivery.dropoff.lng);
    }
  }, [delivery]);

  // Fetch real ETA using OSRM API
  const fetchRealETA = async (originLat, originLng, destLat, destLng) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users/eta`, {
        params: {
          origin_lat: originLat,
          origin_lng: originLng,
          dest_lat: destLat,
          dest_lng: destLng
        }
      });
      
      if (response.data) {
        setEta(response.data.duration_minutes);
        setEstimatedDistance(response.data.distance_miles);
      }
    } catch (error) {
      console.error('Error fetching ETA:', error);
      // Fallback to simple distance calculation
      const distance = calculateDistance(originLat, originLng, destLat, destLng);
      setEta(Math.round(distance / 0.5)); // ~30 mph average
    }
  };

  // State for distance
  const [estimatedDistance, setEstimatedDistance] = useState(null);

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Connect to WebSocket for real-time updates
  const connectWebSocket = useCallback(() => {
    if (!delivery?.user_id) return;
    
    const wsUrl = BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/user/${delivery.user_id}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected for tracking');
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Handle driver location update
        if (data.type === 'driver_location' && data.ride_id === deliveryId) {
          setDriverLocation({ lat: data.lat, lng: data.lng });
          updateDriverMarker(data.lat, data.lng);
        }
        
        // Handle status update
        if (data.type === 'ride_status_update' && data.ride_id === deliveryId) {
          setDelivery(prev => ({ ...prev, status: data.status }));
          
          if (data.status === 'delivered') {
            toast({ title: "Package Delivered!", description: "Your delivery has been completed." });
            // Show browser notification
            showPushNotification('Package Delivered!', 'Your delivery has been completed.');
          }
        }
        
        // Handle driver assigned
        if (data.type === 'driver_assigned' && data.ride_id === deliveryId) {
          fetchDelivery(); // Refresh to get driver details
          toast({ title: "Driver Assigned!", description: "A driver has accepted your delivery." });
          showPushNotification('Driver Assigned!', 'A driver is on the way to pick up your package.');
        }
        
        // Handle driver approaching notifications
        if ((data.type === 'driver_approaching' || data.type === 'delivery_approaching') && data.ride_id === deliveryId) {
          toast({ 
            title: data.title || "Driver Almost There!", 
            description: data.message || `Your driver is ${data.eta_minutes || 'a few'} minutes away` 
          });
          showPushNotification(data.title, data.message);
        }
        
        // Handle delivery photo uploaded
        if (data.type === 'delivery_photo_uploaded' && data.ride_id === deliveryId) {
          setDelivery(prev => ({ ...prev, delivery_photo: data.photo_url }));
          toast({ title: "Proof of Delivery", description: "Driver has uploaded a delivery photo." });
        }
      } catch (e) {
        console.error('WebSocket message error:', e);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    ws.onclose = () => {
      console.log('WebSocket closed, reconnecting...');
      setTimeout(connectWebSocket, 3000);
    };
    
    wsRef.current = ws;
  }, [delivery, deliveryId, fetchDelivery, updateDriverMarker]);

  // Initial fetch
  useEffect(() => {
    fetchDelivery();
    
    // Load Leaflet CSS
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    
    // Load Leaflet JS
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => initializeMap();
      document.body.appendChild(script);
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [fetchDelivery, initializeMap]);

  // Initialize map when delivery loads
  useEffect(() => {
    if (delivery && window.L && !mapRef.current) {
      initializeMap();
    }
  }, [delivery, initializeMap]);

  // Connect WebSocket when delivery loads
  useEffect(() => {
    if (delivery && !wsRef.current) {
      connectWebSocket();
    }
  }, [delivery, connectWebSocket]);

  // Poll for updates every 10 seconds as backup
  useEffect(() => {
    if (!delivery) return;
    
    const interval = setInterval(fetchDelivery, 10000);
    return () => clearInterval(interval);
  }, [delivery, fetchDelivery]);

  // Get current step index
  const getCurrentStepIndex = () => {
    if (!delivery) return 0;
    const status = delivery.status || 'pending_payment';
    const index = DELIVERY_STEPS.findIndex(s => s.key === status);
    return index >= 0 ? index : 0;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading delivery details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Delivery Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/delivery-app')}>
            Back to Delivery App
          </Button>
        </Card>
      </div>
    );
  }

  const currentStep = getCurrentStepIndex();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-4 sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/delivery-app')}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft size={20} />
          </Button>
          <div className="flex items-center gap-3">
            <Package size={24} className="text-amber-400" />
            <div>
              <h1 className="text-lg font-bold">Track Delivery</h1>
              <p className="text-xs text-gray-400">ID: {deliveryId?.slice(0, 8)}...</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Map */}
        <Card className="mb-6 overflow-hidden rounded-2xl shadow-lg">
          <div id="tracking-map" className="h-64 md:h-80 w-full bg-gray-100" />
          
          {/* ETA Banner */}
          {eta && delivery?.status !== 'delivered' && (
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock size={24} />
                <div>
                  <p className="text-sm opacity-90">Estimated Arrival</p>
                  <p className="text-2xl font-bold">{eta} min</p>
                </div>
              </div>
              <div className="text-right">
                {estimatedDistance && (
                  <p className="text-lg font-semibold">{estimatedDistance.toFixed(1)} mi</p>
                )}
                {driverLocation && (
                  <p className="text-xs opacity-80 flex items-center gap-1 justify-end">
                    <span className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></span>
                    Live tracking
                  </p>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Status Progress */}
        <Card className="mb-6 p-6 rounded-2xl shadow-lg">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Delivery Status</h2>
          
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
            <div 
              className="absolute left-4 top-0 w-0.5 bg-amber-500 transition-all duration-500"
              style={{ height: `${(currentStep / (DELIVERY_STEPS.length - 1)) * 100}%` }}
            />
            
            {/* Steps */}
            <div className="space-y-6">
              {DELIVERY_STEPS.map((step, index) => {
                const StepIcon = step.icon;
                const isCompleted = index <= currentStep;
                const isCurrent = index === currentStep;
                
                return (
                  <div key={step.key} className="flex items-start gap-4 relative">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all ${
                      isCompleted 
                        ? 'bg-amber-500 text-white' 
                        : 'bg-gray-200 text-gray-400'
                    } ${isCurrent ? 'ring-4 ring-amber-200' : ''}`}>
                      <StepIcon size={16} />
                    </div>
                    <div className={`flex-1 pb-2 ${isCompleted ? 'text-gray-800' : 'text-gray-400'}`}>
                      <p className={`font-medium ${isCurrent ? 'text-amber-600' : ''}`}>
                        {step.label}
                        {isCurrent && <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Current</span>}
                      </p>
                      <p className="text-sm">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Driver Info */}
        {driver && delivery?.status !== 'pending_payment' && delivery?.status !== 'searching_driver' && (
          <Card className="mb-6 p-6 rounded-2xl shadow-lg">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Your Driver</h2>
            
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-2xl font-bold">
                {driver.name?.charAt(0) || 'D'}
              </div>
              <div className="flex-1">
                <p className="font-bold text-lg">{driver.name}</p>
                <p className="text-gray-500 text-sm">{delivery.vehicle_type}</p>
                {driver.rating && (
                  <div className="flex items-center gap-1 text-amber-500 mt-1">
                    <span>★</span>
                    <span className="text-sm font-medium">{driver.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="icon"
                  className="rounded-full"
                  onClick={() => window.open(`tel:${driver.phone}`)}
                >
                  <Phone size={20} />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  className="rounded-full"
                  onClick={() => toast({ title: "Chat", description: "Chat feature coming soon" })}
                >
                  <MessageCircle size={20} />
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Delivery Details */}
        <Card className="p-6 rounded-2xl shadow-lg">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Delivery Details</h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <MapPin size={16} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">Pickup</p>
                <p className="text-gray-800">{delivery?.pickup?.address || 'N/A'}</p>
              </div>
            </div>
            
            {/* Multi-stop display */}
            {delivery?.stops && delivery.stops.length > 0 && (
              <div className="ml-4 border-l-2 border-dashed border-amber-300 pl-4 space-y-3">
                {delivery.stops.map((stop, index) => {
                  const isCompleted = delivery.completed_stops?.includes(index);
                  return (
                    <div key={index} className="flex items-start gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs ${isCompleted ? 'bg-gray-400' : 'bg-amber-500'}`}>
                        {isCompleted ? '✓' : index + 1}
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-medium">
                          Stop {index + 1} {isCompleted && <span className="text-green-600">(Completed)</span>}
                        </p>
                        <p className={`${isCompleted ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                          {stop?.address || `Stop ${index + 1}`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <MapPin size={16} className="text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">Dropoff</p>
                <p className="text-gray-800">{delivery?.dropoff?.address || 'N/A'}</p>
              </div>
            </div>
            
            {delivery?.special_instructions && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Package size={16} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium">Package Notes</p>
                  <p className="text-gray-800">{delivery.special_instructions}</p>
                </div>
              </div>
            )}
            
            <div className="pt-4 border-t flex justify-between items-center">
              <span className="text-gray-500">Total</span>
              <span className="text-2xl font-bold text-amber-600">
                ${delivery?.estimated_price?.toFixed(2) || '0.00'}
              </span>
            </div>
          </div>
        </Card>
        
        {/* Proof of Delivery Photo */}
        {delivery?.delivery_photo && (
          <Card className="mt-6 p-6 rounded-2xl shadow-lg">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <CheckCircle className="text-green-500" size={20} />
              Proof of Delivery
            </h2>
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <img 
                src={delivery.delivery_photo.startsWith('/api') ? `${BACKEND_URL}${delivery.delivery_photo}` : delivery.delivery_photo}
                alt="Proof of delivery"
                className="w-full h-auto max-h-80 object-cover"
              />
            </div>
            {delivery.delivery_photo_timestamp && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                Delivered at {new Date(delivery.delivery_photo_timestamp).toLocaleString()}
              </p>
            )}
          </Card>
        )}
      </main>
      
      {/* Rating Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm p-6 rounded-2xl shadow-2xl bg-white">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Delivery Complete!</h3>
              <p className="text-gray-500 text-sm mt-1">How was your delivery experience?</p>
            </div>
            
            {/* Star Rating */}
            <div className="flex justify-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setSelectedRating(star)}
                  className="focus:outline-none transition-transform hover:scale-110"
                >
                  <svg
                    className={`w-10 h-10 ${selectedRating >= star ? 'text-amber-400' : 'text-gray-300'}`}
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </button>
              ))}
            </div>
            
            {selectedRating > 0 && (
              <p className="text-center text-sm text-gray-600 mb-4">
                {selectedRating === 5 ? '🌟 Excellent!' : 
                 selectedRating === 4 ? '👍 Great!' : 
                 selectedRating === 3 ? '👌 Good' : 
                 selectedRating === 2 ? '😕 Could be better' : 
                 '😞 Poor'}
              </p>
            )}
            
            {/* Comment Input */}
            <textarea
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              placeholder="Add a comment (optional)"
              className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400 mb-4"
              rows={3}
            />
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowRatingModal(false)}
              >
                Skip
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                onClick={submitRating}
                disabled={isSubmittingRating || selectedRating === 0}
              >
                {isSubmittingRating ? (
                  <><Loader2 className="animate-spin mr-2" size={16} /> Submitting...</>
                ) : (
                  'Submit Rating'
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DeliveryTracking;
